import React, { useState, useEffect } from 'react';
import { Menu, X, Zap, Play } from 'lucide-react';
import { useReducedMotion } from '../hooks/useReducedMotion';

interface NavigationProps {
  className?: string;
}

export default function Navigation({ className = '' }: NavigationProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [isScrolled, setIsScrolled] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  const menuItems = [
    { id: 'services', label: 'Services', href: '#services' },
    { id: 'about', label: 'About', href: '#about' },
    { id: 'portfolio', label: 'Portfolio', href: '#portfolio' },
    { id: 'contact', label: 'Contact', href: '#contact' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);

      // Find active section
      const sections = ['hero', 'services', 'about', 'portfolio', 'contact'];
      let currentSection = 'hero';

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            currentSection = section;
            break;
          }
        }
      }

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
      });
    }
    setIsOpen(false);
  };

  const handleLogoClick = () => {
    window.scrollTo({
      top: 0,
      behavior: prefersReducedMotion ? 'auto' : 'smooth'
    });
    setIsOpen(false);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-black/95 backdrop-blur-lg border-b border-gray-800/50 shadow-lg shadow-cyan-500/10' 
        : 'bg-transparent'
    } ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <button
            onClick={handleLogoClick}
            className="flex items-center gap-2 group transition-all duration-300 hover:scale-105"
          >
            <div className="relative">
              <Zap className={`h-6 w-6 sm:h-8 sm:w-8 text-cyan-400 ${!prefersReducedMotion ? 'group-hover:animate-pulse' : ''}`} />
              {!prefersReducedMotion && (
                <div className="absolute inset-0 bg-cyan-400 rounded-full blur-sm opacity-30 group-hover:opacity-50 transition-opacity"></div>
              )}
            </div>
            <div className="text-lg sm:text-xl font-black">
              <span className="text-cyan-400">MEDIA</span>
              <span className="text-white">MOGULZ</span>
            </div>
          </button>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-1">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${
                  activeSection === item.id
                    ? 'text-cyan-400 bg-cyan-400/10 border border-cyan-400/30'
                    : 'text-gray-300 hover:text-white hover:bg-gray-800/50'
                }`}
              >
                {item.label}
              </button>
            ))}

            {/* Play Button */}
            <button
              onClick={() => setShowVideo(true)}
              className="ml-2 px-4 py-2 rounded-lg font-semibold transition-all duration-300 bg-cyan-600/80 hover:bg-cyan-600 text-white flex items-center gap-2"
              title="Play hero video"
            >
              <Play className="h-4 w-4" />
              <span>Play</span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors"
            aria-label="Toggle menu"
          >
            {isOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-black/95 backdrop-blur-lg border-t border-gray-800/50">
          <div className="px-4 py-4 space-y-2">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`w-full text-left px-4 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  activeSection === item.id
                    ? 'text-cyan-400 bg-cyan-400/10 border border-cyan-400/30'
                    : 'text-gray-300 hover:text-white hover:bg-gray-800/50'
                }`}
              >
                {item.label}
              </button>
            ))}

            {/* Mobile Play Button */}
            <button
              onClick={() => { setIsOpen(false); setShowVideo(true); }}
              className="w-full px-4 py-3 rounded-lg font-semibold transition-all duration-300 bg-cyan-600/80 hover:bg-cyan-600 text-white flex items-center gap-2 justify-center"
              title="Play hero video"
            >
              <Play className="h-4 w-4" />
              <span>Play</span>
            </button>
          </div>
        </div>
      )}

      {/* Inline Video Overlay */}
      {showVideo && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
          onClick={() => setShowVideo(false)}
        >
          <div
            className="relative w-full max-w-4xl bg-black rounded-lg overflow-hidden shadow-xl border border-gray-800"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowVideo(false)}
              className="absolute top-2 right-2 z-10 p-2 rounded bg-gray-900/80 hover:bg-gray-800 text-gray-200"
              aria-label="Close video"
            >
              <X className="h-5 w-5" />
            </button>
            <video
              src="/hero-video.mp4"
              controls
              autoPlay
              playsInline
              className="w-full h-full"
              preload="auto"
            />
          </div>
        </div>
      )}
    </nav>
  );
}
